import React, { useCallback } from 'react';
import { css } from '@compiled/react';

import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import type { CommentData as LegacyCommentData } from '@confluence/comments-data/entry-points/useCommentsData';
import type { CommentData as NewCommentData } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils';import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { CommentBatchList } from '@confluence/comment/entry-points/CommentBatchList.ts';
import { getCommentThreadsToBatch } from '@confluence/comments-panel/entry-points/commentThreadHelper.ts';

import { ObjectCommentThread } from './ObjectCommentThread.tsx';

type CommentListProps = {
	commentThreads: (LegacyCommentData | NewCommentData)[];
	supportedTopLevelActions: CommentAction[];
	flags?: FlagsStateContainer;
	onAllCommentsRendered?: () => void;
	onCommentBatchRendered?: () => void;
	isOffline?: boolean;
};

const listStyles = css({
	overflowY: 'visible', // Required for overflowing editor menus
	overflowX: 'visible', // Even though overflow is mostly in Y direction, overflow will break w/o this line
	flexGrow: 1,
	display: 'block',
});

// this component holds a list of comments
export const ObjectCommentsList = ({
	commentThreads,
	supportedTopLevelActions,
	flags,
	onAllCommentsRendered,
	onCommentBatchRendered,
	isOffline,
}: CommentListProps) => {
	const isLastThread = useCallback(
		(index: number) => index === commentThreads.length - 1,
		[commentThreads],
	);

	// Props we want the batch renderer to pass through that aren't comment specific
	const passThroughProps = {
		supportedTopLevelActions,
		flags,
		onLastThreadRendered: onAllCommentsRendered,
		onCommentBatchRendered,
		isLastThread,
		isOffline,
	};

	return (
		<div
			id="object-comments-list-container"
			data-testid="object-comments-list-container"
			css={listStyles}
		>
			<CommentBatchList
				commentsList={commentThreads}
				passThroughProps={passThroughProps}
				Component={ObjectCommentThread}
				getElementsToBatch={getCommentThreadsToBatch}
			/>
		</div>
	);
};
